/**
 * ArtByCharacter.jsx — Phase 8 "By Character" tab (2026-02).
 *
 * Grid view (4-col desktop) + List view. Card click → right-side
 * detail drawer with portrait gallery + editable art_notes + character
 * props grouped by sub-category + scenes strip.
 *
 * Character prop chips are asset-backed (always) — rendered via the
 * shared Chip renderer with 🔗 badge.
 *
 * `+ Add character prop` in each sub-category subsection opens the
 * shared AssetModal (via AssetActionsContext) with `character_id` +
 * `character_sub_category` pre-filled. This is the 4th `+ Add asset`
 * entry point (previous 3: header, ArtByScene drawer, ArtByLocation).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { X, Search, Upload, Grid2X2, List as ListIcon, MapPin } from 'lucide-react';
import { AuthImage } from '../../components/AuthImage';
import { Textarea } from '../../components/ui/textarea';
import { formatSceneNumber } from '../../utils/sceneNumber';
import { useProjectRole } from '../../hooks/useProjectRole';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Chip, TypeBadge, TodBadge } from './artShared';
import { useAssetActions } from './AssetActionsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_TOKENS = {
  LEAD:       { label: 'LEAD',       bg: 'rgba(129,140,248,.24)', fg: '#818CF8' },
  SUPPORTING: { label: 'SUPPORTING', bg: 'rgba(59,130,246,.22)',  fg: '#60A5FA' },
  EXTRA:      { label: 'EXTRA',      bg: 'rgba(148,163,184,.18)', fg: '#94A3B8' },
};
const SUB_LABELS = {
  hero_prop:          'Hero Props',
  wardrobe_accessory: 'Wardrobe Accessories',
  personal_effect:    'Personal Effects',
  other:              'Other',
};

function RolePill({ role }) {
  const tok = ROLE_TOKENS[role] || ROLE_TOKENS.SUPPORTING;
  return (
    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase"
      style={{ background: tok.bg, color: tok.fg }} data-testid={`role-pill-${role}`}>
      {tok.label}
    </span>
  );
}

function CharacterCard({ c, onOpen, compact = false }) {
  const hero = c.portrait_photos?.[0]?.storage_path
            || c.actor?.actor_headshot_path
            || null;
  return (
    <button type="button" onClick={() => onOpen(c)}
      className="text-left rounded-lg overflow-hidden border border-[var(--color-border)] bg-[var(--color-panel)] hover:border-[var(--color-accent)] transition"
      data-testid={`character-card-${c.id}`}
      data-density={compact ? 'compact' : 'comfortable'}>
      <div className="relative aspect-[3/4] bg-[var(--color-bg)]">
        {hero ? (
          <AuthImage path={hero} className="w-full h-full object-cover" alt="" />
        ) : (
          <div className={`w-full h-full flex items-center justify-center text-[var(--color-text-mute)] ${compact ? 'text-2xl' : 'text-4xl'}`}>👤</div>
        )}
        <div className="absolute top-2 right-2">
          <RolePill role={c.character_type || 'SUPPORTING'} />
        </div>
      </div>
      <div className={compact ? 'p-1.5' : 'p-2'}>
        <div className={`font-bold text-[var(--color-text)] truncate ${compact ? 'text-[11px]' : 'text-sm'}`}>{c.name}</div>
        {!compact && (
          <div className="text-[11px] italic text-[var(--color-text-dim)] truncate">
            {c.actor?.actor_name ? `played by ${c.actor.actor_name}` : '—'}
          </div>
        )}
        <div className={`text-[var(--color-text-dim)] mt-1 flex gap-2 ${compact ? 'text-[9px]' : 'text-[10px]'}`}>
          <span>🎬 {c.scene_count}</span>
          <span>🎒 {c.prop_count}</span>
        </div>
        {!compact && (
          <div className="text-[11px] text-[var(--color-accent)] text-right mt-1">View character →</div>
        )}
      </div>
    </button>
  );
}

function CharacterDrawer({ character, projectId, token, canWrite, onClose, onMutated }) {
  const authAxios = useMemo(() => axios.create({
    baseURL: API, headers: { Authorization: `Bearer ${token}` },
  }), [token]);
  const { openAssetModal } = useAssetActions();

  const [detail, setDetail] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const photoRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const r = await authAxios.get(`/projects/${projectId}/art/characters/${character.id}`);
      setDetail(r.data);
      setNotesDraft(r.data?.art_notes || '');
      setNotesDirty(false);
    } catch (e) { console.error(e); toast.error('Failed to load character'); }
  }, [authAxios, projectId, character.id]);
  useEffect(() => { reload(); }, [reload]);

  const saveNotes = async () => {
    try {
      await authAxios.patch(`/projects/${projectId}/art/characters/${character.id}`,
        { art_notes: notesDraft });
      setNotesDirty(false);
      toast.success('Notes saved');
      onMutated?.();
    } catch (e) { console.error(e); toast.error('Save failed'); }
  };
  const uploadPortrait = () => photoRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await authAxios.post(
        `/projects/${projectId}/art/characters/${character.id}/portrait-photos`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Photo added');
      await reload();
      onMutated?.();
    } catch (err) { console.error(err); toast.error('Upload failed'); }
  };
  const removePortrait = async (fid) => {
    if (!window.confirm('Remove this photo from the gallery?')) return;
    try {
      await authAxios.delete(
        `/projects/${projectId}/art/characters/${character.id}/portrait-photos/${fid}`,
      );
      await reload();
      onMutated?.();
    } catch (e) { console.error(e); toast.error('Remove failed'); }
  };

  const addProp = (sub) => {
    openAssetModal({
      prefill: {
        category: 'action_prop',
        character_id: character.id,
        character_sub_category: sub,
      },
      onCreated: async () => { await reload(); onMutated?.(); },
    });
  };

  if (!detail) return (
    <div className="fixed inset-0 z-40 flex" data-testid="character-drawer"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="flex-1 bg-black/50 pointer-events-none" aria-hidden="true" />
      <aside className="w-[600px] max-w-full h-full bg-[var(--color-bg)] border-l border-[var(--color-border)] p-4"
             onClick={(e) => e.stopPropagation()}>
        <div className="text-[var(--color-text-dim)] text-sm">Loading…</div>
      </aside>
    </div>
  );

  const cover = detail.portrait_photos?.[0]?.storage_path
             || detail.actor?.actor_headshot_path;

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="character-drawer"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      {/* Phase 12b.2 — backdrop pointer-events-none; click-to-close on outer wrapper. */}
      <div className="flex-1 bg-black/50 pointer-events-none" aria-hidden="true" />
      <aside className="w-[600px] max-w-full h-full bg-[var(--color-bg)] border-l border-[var(--color-border)] overflow-y-auto flex flex-col"
             onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 flex items-center gap-2 px-4 py-3 bg-[var(--color-bg)] border-b border-[var(--color-border)] z-10">
          <span className="text-lg font-bold text-[var(--color-accent)]">{detail.name}</span>
          <RolePill role={detail.character_type || 'SUPPORTING'} />
          {detail.actor?.actor_name && (
            <span className="text-[11px] italic text-[var(--color-text-dim)] truncate">· played by {detail.actor.actor_name}</span>
          )}
          <button onClick={onClose} className="ml-auto text-[var(--color-text-dim)] hover:text-white" data-testid="character-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <section className="p-4 border-b border-[var(--color-border)]">
          <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-2 flex items-center gap-2">
            <span>Portrait gallery ({detail.portrait_photos.length})</span>
            {canWrite && (
              <button type="button" onClick={uploadPortrait}
                className="ml-auto text-[10px] px-2 py-0.5 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
                data-testid="character-drawer-upload-photo">
                <Upload className="w-3 h-3 inline mr-1" /> Upload
              </button>
            )}
          </div>
          <input ref={photoRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
          <div className="flex gap-1.5 flex-wrap">
            {detail.portrait_photos.map(p => (
              <div key={p.file_id} className="relative group">
                <div className="w-[100px] h-[100px] rounded overflow-hidden border border-[var(--color-border)]">
                  <AuthImage path={p.storage_path} className="w-full h-full object-cover" alt="" />
                </div>
                {canWrite && (
                  <button type="button" onClick={() => removePortrait(p.file_id)}
                    className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 bg-black/70 text-[var(--color-priority-critical)] rounded p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {detail.portrait_photos.length === 0 && !cover && (
              <span className="text-[11px] italic text-[var(--color-text-mute)]">No portrait yet.</span>
            )}
            {detail.portrait_photos.length === 0 && cover && (
              <div className="w-[100px] h-[100px] rounded overflow-hidden border border-[var(--color-border)] relative"
                   title="Actor headshot from Casting">
                <AuthImage path={cover} className="w-full h-full object-cover opacity-70" alt="" />
                <div className="absolute inset-x-0 bottom-0 text-[8px] text-center bg-black/60 text-[var(--color-text-dim)]">Actor · Casting</div>
              </div>
            )}
          </div>
        </section>

        <section className="p-4 border-b border-[var(--color-border)]">
          <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-2 flex items-center gap-2">
            <span>Bio / Art notes</span>
            {notesDirty && <span className="text-[9px] text-[var(--color-accent)]">• unsaved</span>}
          </div>
          <Textarea
            value={notesDraft}
            disabled={!canWrite}
            onChange={(e) => { setNotesDraft(e.target.value); setNotesDirty(true); }}
            placeholder={canWrite ? 'Wardrobe notes, mannerisms, character signature items…' : 'No notes.'}
            className="min-h-[80px] bg-[var(--color-bg)] border border-[var(--color-border)] text-[12px] text-[var(--color-text)]"
            data-testid="character-drawer-notes"
          />
          {canWrite && notesDirty && (
            <div className="mt-2 flex justify-end gap-1">
              <button type="button" onClick={() => { setNotesDraft(detail.art_notes || ''); setNotesDirty(false); }}
                className="text-[10px] px-2 py-0.5 rounded bg-[var(--color-panel)] text-[var(--color-text-sec)]">Cancel</button>
              <button type="button" onClick={saveNotes}
                className="text-[10px] px-2 py-0.5 rounded bg-[var(--color-accent)] text-black font-bold"
                data-testid="character-drawer-save-notes">Save</button>
            </div>
          )}
        </section>

        <section className="p-4 border-b border-[var(--color-border)]">
          <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-2">Character Props</div>
          {['hero_prop', 'wardrobe_accessory', 'personal_effect', 'other'].map(sub => {
            const rows = detail.assets_by_sub?.[sub] || [];
            return (
              <div key={sub} className="mb-3">
                <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--color-text-sec)] mb-1 flex items-center gap-1">
                  <span>{SUB_LABELS[sub]} ({rows.length})</span>
                  {canWrite && (
                    <button type="button" onClick={() => addProp(sub)}
                      className="ml-auto text-[10px] px-2 py-0.5 rounded border border-[var(--color-border-strong)] text-[var(--color-accent)] hover:bg-[var(--color-panel)]"
                      data-testid={`character-add-prop-${sub}`}>
                      + Add character prop
                    </button>
                  )}
                </div>
                <div className="flex gap-1 flex-wrap">
                  {rows.length === 0 && <span className="text-[11px] italic text-[var(--color-text-mute)]">—</span>}
                  {rows.map(a => (
                    <Chip
                      key={a.id}
                      item={{
                        id: a.id,
                        description: a.name,
                        asset_id: a.id,
                        image_paths: (a.photos || []).map(p => p.storage_path),
                      }}
                      canWrite={false}   // asset editing happens via Assets tab
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </section>

        <section className="p-4">
          <div className="text-[10px] font-bold uppercase text-[var(--color-text-dim)] mb-2">
            Scenes ({(detail.scenes || []).length})
          </div>
          <div className="flex gap-2 flex-wrap">
            {(detail.scenes || []).map(s => (
              <div key={s.id} className="w-[180px] p-2 rounded border border-[var(--color-border)] bg-[var(--color-panel)]"
                   data-testid={`character-scene-card-${s.scene_number}`}>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[var(--color-scene-props)] text-black">
                    #{formatSceneNumber(s.scene_number)}
                  </span>
                  <TypeBadge type={s.location_type} />
                  <TodBadge tod={s.time_of_day} />
                </div>
                <div className="text-[10px] text-[var(--color-text-sec)] truncate flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-[var(--color-text-dim)]" />{s.location_name || '—'}
                </div>
                <div className="text-[10px] text-[var(--color-text-sec)] line-clamp-2 mt-1">
                  {s.synopsis_en || s.synopsis || '—'}
                </div>
              </div>
            ))}
          </div>
        </section>
      </aside>
    </div>
  );
}

export default function ArtByCharacter({ projectId, token }) {
  const [view, setView] = useState(() => localStorage.getItem('art.by_character.view') || 'grid');
  const [density, setDensity] = useState(() => localStorage.getItem('art.by_character.density') || 'comfortable');
  useEffect(() => { localStorage.setItem('art.by_character.density', density); }, [density]);
  useEffect(() => { localStorage.setItem('art.by_character.view', view); }, [view]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const { permissions } = useProjectRole(projectId);
  const { user } = useAuth();
  // Art module (2026-07) — write affordances open to any authenticated user
  // with project access. Backend still enforces auth.
  const canWrite = !!user || !!permissions?.canWrite;

  const authAxios = useMemo(() => axios.create({
    baseURL: API, headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (roleFilter) params.role_tag = roleFilter;
      const r = await authAxios.get(`/projects/${projectId}/art/characters`, { params });
      setItems(r.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authAxios, projectId, search, roleFilter]);
  useEffect(() => { reload(); }, [reload]);

  const roleCounts = useMemo(() => {
    const c = {};
    for (const x of items) c[x.character_type] = (c[x.character_type] || 0) + 1;
    return c;
  }, [items]);

  return (
    <div className="p-4" data-testid="art-by-character-view">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 border border-[var(--color-border)] rounded px-2 py-1 bg-[var(--color-bg)]">
          <Search className="w-3 h-3 text-[var(--color-text-dim)]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search characters…"
            className="bg-transparent border-0 h-6 text-xs w-56 outline-none text-[var(--color-text)]"
            data-testid="art-char-search" />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
          className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-xs h-8 px-2 text-[var(--color-text-sec)]"
          data-testid="art-char-role-filter">
          <option value="">All roles</option>
          <option value="LEAD">Lead ({roleCounts.LEAD || 0})</option>
          <option value="SUPPORTING">Supporting ({roleCounts.SUPPORTING || 0})</option>
          <option value="EXTRA">Extra ({roleCounts.EXTRA || 0})</option>
        </select>
        <span className="text-[10px] text-[var(--color-text-dim)] ml-2">{items.length} character{items.length === 1 ? '' : 's'}</span>
        {items.length >= 20 && density === 'comfortable' && (
          <span className="text-[9px] text-[var(--color-accent)] italic ml-1"
                data-testid="art-char-compact-hint"
                title="Toggle Compact view for tighter layout">
            Try Compact →
          </span>
        )}
        <div className="ml-auto flex gap-2 items-center">
          {view === 'grid' && (
            <div className="flex gap-1 border border-[var(--color-border)] rounded overflow-hidden">
              <button onClick={() => setDensity('comfortable')}
                className={`px-2 py-1 text-[10px] ${density === 'comfortable' ? 'bg-[var(--color-accent)] text-black font-bold' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
                data-testid="art-char-density-comfortable">
                Comfortable
              </button>
              <button onClick={() => setDensity('compact')}
                className={`px-2 py-1 text-[10px] ${density === 'compact' ? 'bg-[var(--color-accent)] text-black font-bold' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
                data-testid="art-char-density-compact">
                Compact
              </button>
            </div>
          )}
          <div className="flex gap-1 border border-[var(--color-border)] rounded overflow-hidden">
            <button onClick={() => setView('grid')}
              className={`px-2 py-1 text-xs ${view === 'grid' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
              data-testid="art-char-view-grid">
              <Grid2X2 className="w-3 h-3" />
            </button>
            <button onClick={() => setView('list')}
              className={`px-2 py-1 text-xs ${view === 'list' ? 'bg-[var(--color-accent)] text-black' : 'bg-[var(--color-bg)] text-[var(--color-text-sec)]'}`}
              data-testid="art-char-view-list">
              <ListIcon className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="text-center text-[var(--color-text-dim)] text-sm py-8">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center text-[var(--color-text-dim)] text-sm py-12" data-testid="art-char-empty">
          No characters found. Characters are auto-parsed from your script.
        </div>
      )}

      {!loading && items.length > 0 && view === 'grid' && (
        <div className={density === 'compact'
              ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2'
              : 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3'}
             data-testid="art-char-grid"
             data-density={density}>
          {items.map(c => <CharacterCard key={c.id} c={c} onOpen={setSelected} compact={density === 'compact'} />)}
        </div>
      )}

      {!loading && items.length > 0 && view === 'list' && (
        <table className="w-full text-sm border border-[var(--color-border)] rounded" data-testid="art-char-table">
          <thead>
            <tr className="text-[10px] uppercase text-[var(--color-text-dim)] bg-[var(--color-bg)] border-b border-[var(--color-border)]">
              <th className="px-2 py-1.5 text-left w-14">Portrait</th>
              <th className="px-2 py-1.5 text-left">Name</th>
              <th className="px-2 py-1.5 text-left">Role</th>
              <th className="px-2 py-1.5 text-left">Actor</th>
              <th className="px-2 py-1.5 text-left">Scenes</th>
              <th className="px-2 py-1.5 text-left">Props</th>
            </tr>
          </thead>
          <tbody>
            {items.map(c => {
              const hero = c.portrait_photos?.[0]?.storage_path
                        || c.actor?.actor_headshot_path;
              return (
                <tr key={c.id} onClick={() => setSelected(c)}
                    className="border-b border-[var(--color-border)] hover:bg-[var(--color-panel)] cursor-pointer"
                    data-testid={`character-row-${c.id}`}>
                  <td className="px-2 py-1.5">
                    <div className="w-10 h-10 rounded overflow-hidden bg-[var(--color-bg)] border border-[var(--color-border)]">
                      {hero ? <AuthImage path={hero} className="w-full h-full object-cover" alt="" />
                            : <div className="w-full h-full flex items-center justify-center text-[var(--color-text-mute)]">👤</div>}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 text-sm text-[var(--color-text)]">{c.name}</td>
                  <td className="px-2 py-1.5"><RolePill role={c.character_type || 'SUPPORTING'} /></td>
                  <td className="px-2 py-1.5 text-xs text-[var(--color-text-sec)]">{c.actor?.actor_name || '—'}</td>
                  <td className="px-2 py-1.5 text-xs text-[var(--color-text-sec)]">🎬 {c.scene_count}</td>
                  <td className="px-2 py-1.5 text-xs text-[var(--color-text-sec)]">🎒 {c.prop_count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {selected && (
        <CharacterDrawer
          character={selected}
          projectId={projectId}
          token={token}
          canWrite={canWrite}
          onClose={() => setSelected(null)}
          onMutated={reload}
        />
      )}
    </div>
  );
}
